// THE ONE CHECKOUT DECIDER (2026-07-18).
//
// Every client payment surface used to ask the client to pick a rail from a
// three-way grid — "Use wallet" / "Pay by card" / "Wallet + card" — with each
// tile disabled under different conditions and each restating the split in its
// own words. That is three decisions to make a payment, and two of the three
// options are only ever valid in mutually exclusive circumstances anyway.
//
// The client now makes ONE decision: "do I want my wallet money used?" — a
// single toggle, ON by default whenever there is anything in the wallet, hidden
// entirely when there is not. The RAIL is then arithmetic, not a choice:
//
//   toggle on,  wallet covers the whole amount   → 'wallet'      (no card leg)
//   toggle on,  wallet covers part of it         → 'wallet_card' (split)
//   toggle off, or nothing available in wallet   → 'card'
//
// The returned `method` / `wallet_portion` are exactly the values the existing
// endpoints already accept, so this is a UI simplification over an unchanged
// money contract. The server re-reads the balance and clamps the wallet portion
// authoritatively on every call — the figures here are for DISPLAY and for
// choosing the rail, never the authority on what is debited.

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100;

// Tolerance for "is there effectively any money here". Matches the 0.005 used
// by walletFigures and by the adjustment/booking gates, i.e. half a fils.
const EPS = 0.005;

/**
 * Resolve the whole checkout from an amount, the wallet's available balance
 * and the toggle.
 *
 * @param {object} opts
 * @param {number} opts.amount     What is being paid, AED.
 * @param {number} opts.spendable  walletFigures.availableBalance() — the ONE
 *                                 wallet figure (total − reserved − payouts;
 *                                 owner ruling 2026-07-19: due adjustments
 *                                 never reduce it). May be negative; it is
 *                                 floored here because a negative balance
 *                                 cannot contribute to a payment. The invoice
 *                                 rail alone passes its narrower cash-only
 *                                 ceiling (a bonus-credit rule, not an owed
 *                                 rule).
 * @param {boolean} opts.useWallet The toggle. Ignored when nothing is available.
 * @returns {{method:'wallet'|'card'|'wallet_card', walletPortion:number,
 *            cardPortion:number, amount:number, spendable:number,
 *            walletCovers:boolean, hasWalletMoney:boolean, needsCard:boolean}}
 */
export const resolvePayNow = ({ amount, spendable, useWallet }) => {
    const amt = Math.max(0, round2(amount));
    // A negative available balance cannot pay for anything. Flooring here is a
    // ceiling on an ACTION, not a display figure — the surfaces still render the
    // true signed balance elsewhere (P3: honest negatives).
    const ceiling = Math.max(0, round2(spendable));
    const hasWalletMoney = ceiling > EPS;

    const walletPortion = (useWallet && hasWalletMoney) ? Math.min(ceiling, amt) : 0;
    const cardPortion = round2(amt - walletPortion);

    let method;
    if (walletPortion > EPS && cardPortion <= EPS) method = 'wallet';
    else if (walletPortion > EPS) method = 'wallet_card';
    else method = 'card';

    return {
        method,
        walletPortion: round2(walletPortion),
        cardPortion,
        amount: amt,
        // The floored ceiling itself — what the wallet could contribute AT
        // MOST to anything. combinedCheckoutFigures clamps the combined-order
        // wallet deduction with it, so no surface can ever display a wallet
        // deduction exceeding the money actually there.
        spendable: ceiling,
        // Would the wallet cover the WHOLE amount if the toggle were on?
        walletCovers: ceiling + EPS >= amt && amt > EPS,
        hasWalletMoney,
        needsCard: cardPortion > EPS,
    };
};

/**
 * The request body for the adjustment / invoice / checkout endpoints. Keeps the
 * "only send wallet_portion on a split" rule in one place so no caller invents
 * its own shape.
 */
export const payNowBody = (plan, extra = {}) => {
    const body = { payment_method: plan.method, ...extra };
    if (plan.method === 'wallet_card') body.wallet_portion = plan.walletPortion;
    return body;
};

/**
 * Label for the single primary button. One button, one verb.
 *
 * The button NAMES THE RAILS IT WILL ACTUALLY USE (2026-07-19). The client
 * was seeing a bare "Pay …" and could not tell what was about to happen to
 * their money, so each branch says which rail(s) the derived plan uses:
 *
 *   wallet       → wallet only, no card leg      → "…by Wallet"
 *   card         → card only, wallet not used    → "…by Credit Card"
 *   wallet_card  → BOTH rails apply (a split)    → "…by Credit Card or Wallet"
 *
 * Only the split branch may name both, and it only ever arises when both legs
 * are non-zero — so the label never advertises a rail this payment will not
 * touch. The amount stays in the label: the panel above reconciles it, and a
 * money button with no money on it is a step backwards.
 */
export const payNowLabel = (plan, fmt) => {
    if (plan.method === 'wallet') return `Pay ${fmt(plan.amount)} Now by Wallet`;
    if (plan.method === 'wallet_card') return `Pay ${fmt(plan.amount)} Now by Credit Card or Wallet`;
    return `Pay ${fmt(plan.amount)} Now by Credit Card`;
};

/**
 * The AMOUNTLESS primary-button label for the REQUEST surfaces (create / edit
 * booking), ported from the mobile reference implementation
 * (hooks/clientHooks/checkoutPlan.ts → payNowLabel) so the two surfaces cannot
 * drift. Owner 2026-07-19: "'Pay now by Card or Wallet'".
 *
 * Two rules, both inherited from mobile:
 *
 *  1. NO AMOUNT. The price summary rendered directly above the button already
 *     states every figure, derived from this same plan — one figure, one
 *     arithmetic path. A second copy on the button is a second place to drift.
 *  2. TRUTHFULNESS: the label names ONLY the rails this payment actually
 *     touches. A zero-balance client paying purely by card must not be told
 *     "or Wallet" (it implies money they do not have), and a client whose
 *     wallet settles the lot must not be told "by Card". So it is DERIVED,
 *     never hardcoded:
 *
 *       both rails in play  → 'Pay now by Card or Wallet'
 *       card leg only       → 'Pay now by Card'
 *       wallet settles all  → 'Pay now by Wallet'
 *       nothing due         → 'Pay now'
 *
 * `extras` is the card-only amount riding along with the booking itself — on
 * the portal that is the mandatory pending-invoice settlement, which is charged
 * on the CARD leg even when the wallet covers the whole booking. It therefore
 * counts towards "is there a card leg at all", exactly as `plan.extras` does on
 * mobile.
 *
 * NOTE: this is deliberately separate from payNowLabel above, which the
 * adjustment and invoice surfaces still use with its amount-carrying wording.
 * Those surfaces were not in scope for the copy change.
 */
export const payNowRailLabel = (plan, { extras = 0 } = {}) => {
    const cardTotal = round2(plan.cardPortion + Math.max(0, round2(extras)));
    // Nothing is being collected on either rail.
    if (cardTotal <= EPS && plan.walletPortion <= EPS) return 'Pay now';
    // The wallet clears the whole thing and no card-only extras ride along:
    // there is no card leg, so do not name one.
    if (cardTotal <= EPS) return 'Pay now by Wallet';
    // A real split — wallet money is used AND the gateway is charged.
    if (plan.walletPortion > EPS) return 'Pay now by Card or Wallet';
    // Pure card: no balance, or the toggle is off.
    return 'Pay now by Card';
};

/**
 * A4 (2026-07-19): when pending invoices ride a checkout the
 * sum must read
 *
 *     + Booking amount
 *     + Pending invoices
 *     − Wallet amount
 *     = whatever left to pay
 *
 * — additions FIRST, then ONE wallet deduction against the combined total,
 * then one "left to pay" line. ("right now its doing this instead: + Booking
 * Amount − Wallet amount + Pending Invoices".)
 *
 * This helper derives the combined-order figures from the ONE plan so the
 * host surface adds no second arithmetic path. It mirrors the SERVER's split
 * exactly (and mobile's checkoutPlan.buildCheckoutPlan, the reference
 * implementation of the same arithmetic):
 *
 *   wallet covers booking + P (spendable ≥ combined total, toggle on) —
 *     submitBookingRequest settles P from the wallet itself
 *     (settlePendingFromWallet) before staging the request, so the one wallet
 *     deduction is walletPortion + P and nothing is left to pay;
 *   anything less — /api/payments/request-checkout clamps the wallet leg to
 *     min(available, booking) and captures the remainder PLUS the whole P in
 *     ONE card payment. That includes the band where the wallet covers the
 *     booking but not booking + P: the wallet pays exactly the booking and
 *     the card pays exactly P — the server has no rail that part-pays
 *     invoices from the wallet (settlePendingFromWallet is all-or-nothing,
 *     cash-only; its rail 400s PENDING_INVOICES_DUE below the combined total).
 *
 * MONEY-TRUTH INVARIANTS, in that order and non-negotiable:
 *   · walletDeduction ≤ plan.spendable — the display may never promise a
 *     wallet deduction exceeding the money actually there;
 *   · leftToPay is the exact gateway capture — never 0.00 while a card leg
 *     exists (`needsCard` here is THE card-leg predicate for combined
 *     checkouts; plan.needsCard only knows about the booking).
 *
 * @param {object} plan            resolvePayNow(...) — the one derived plan.
 * @param {number} pendingInvoices P, the mandatory pending-invoice settlement
 *                                 riding this checkout (0 when none).
 */
export const combinedCheckoutFigures = (plan, pendingInvoices = 0) => {
    const pending = Math.max(0, round2(pendingInvoices));
    const combinedTotal = round2(plan.amount + pending);
    // The wallet settles P only when it is in use AND covers the WHOLE
    // combined total — the server accepts nothing in between. This clamp is
    // what keeps the displayed deduction within the actual balance.
    const walletSettlesPending = pending > EPS
        && plan.walletPortion > EPS
        && plan.spendable + EPS >= combinedTotal;
    // ONE wallet deduction, against the combined total, never above spendable.
    const walletDeduction = round2(plan.walletPortion + (walletSettlesPending ? pending : 0));
    // ONE "left to pay": the exact card capture (cardPortion + P on any rail
    // with a card leg — including P alone when the wallet covers the booking).
    const leftToPay = round2(Math.max(0, combinedTotal - walletDeduction));
    return {
        pending,
        combinedTotal,
        walletSettlesPending,
        walletDeduction,
        leftToPay,
        // Does THIS combined checkout have a card leg? Hosts must use this —
        // not plan.needsCard, which ignores P — for routing, labels and notes.
        needsCard: leftToPay > EPS,
    };
};

/**
 * Should the quiet "top up and earn bonuses" nudge be offered? Only when the
 * wallet cannot cover this payment on its own — i.e. there is genuinely
 * something for a top-up to fix. Never shown when the wallet already covers it.
 *
 * THE NUDGE STAYS (item 15, owner 2026-07-19 correction). This predicate was
 * once deleted on a misreading of the owner — the only thing he wanted gone was
 * the duplicate wallet-balance calculation block (item 13), never this link
 * ("keep it only as a text with the arrow"). If this export is missing, that is
 * a DEFECT: restore it and the nudge it feeds.
 */
export const shouldOfferTopup = (plan) => plan.needsCard;

export default resolvePayNow;
